import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/lib/AuthContext";
import { useSubscription } from "@/lib/SubscriptionContext";
import logger from "@/lib/logger";

const UsageContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

const METRIC_LABELS = {
  tasks_created: "Tasks Created",
  focus_sessions: "Focus Sessions",
  habits_completed: "Habits Completed",
  xp_earned: "XP Earned",
};

export function UsageProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { isPro } = useSubscription();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    if (!isAuthenticated) {
      setUsageData(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/usage/daily`, { withCredentials: true });
      setUsageData(data);
    } catch (e) {
      logger.error("fetchUsage:", e);
      setUsageData(null);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  // Re-fetch every 60 seconds to stay fresh
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(fetchUsage, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUsage]);

  const isLimitReached = useCallback((metric) => {
    if (isPro || !usageData || usageData.is_pro) return false;
    const m = usageData.usage?.[metric];
    return m?.reached ?? false;
  }, [isPro, usageData]);

  const getUsageInfo = useCallback((metric) => {
    if (isPro || !usageData || usageData.is_pro) {
      return { current: 0, limit: null, percentage: 0, reached: false, label: METRIC_LABELS[metric] || metric };
    }
    const m = usageData.usage?.[metric];
    if (!m) return { current: 0, limit: null, percentage: 0, reached: false, label: METRIC_LABELS[metric] || metric };
    return { ...m, label: METRIC_LABELS[metric] || metric };
  }, [isPro, usageData]);

  const handleLimitResponse = useCallback((responseData) => {
    // Called after any API response that might include _daily_limit
    if (responseData?._daily_limit) {
      fetchUsage(); // refresh usage data
      return responseData._daily_limit;
    }
    return null;
  }, [fetchUsage]);

  // --- Split value into focused sub-memos (≤5 deps each) ---
  const usageState = useMemo(() => ({
    usageData, loading, isPro: isPro || usageData?.is_pro,
    limits: usageData?.limits || null, metricLabels: METRIC_LABELS,
  }), [usageData, loading, isPro]);

  const usageActions = useMemo(() => ({
    isLimitReached, getUsageInfo, fetchUsage, handleLimitResponse,
  }), [isLimitReached, getUsageInfo, fetchUsage, handleLimitResponse]);

  const value = useMemo(() => ({
    ...usageState, ...usageActions,
  }), [usageState, usageActions]);

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
}

export function useUsage() {
  const ctx = useContext(UsageContext);
  if (!ctx) throw new Error("useUsage must be used within UsageProvider");
  return ctx;
}
